import type { MatchPushSettings, UpdateMatchPushSettingsBody } from '@/types/matchPushes';
import { MOMENT_LABEL, PLAN_MOMENTS } from '@/utils/matchPushCopy';

/** What the settings form holds. */
export type SettingsForm = Omit<MatchPushSettings, 'updatedBy' | 'updatedAt'>;

/** Ranges the backend accepts (match-push-settings.service.ts). */
export const SETTINGS_RANGE = {
  dailyCapPerPlayer: { min: 0, max: 5 },
  windowStartHour: { min: 0, max: 23 },
  windowEndHour: { min: 1, max: 24 },
  holdoutPercent: { min: 0, max: 50 },
  maxInactiveDays: { min: 1, max: 365 },
  skipActiveMinutes: { min: 0, max: 1440 },
} as const;

/** The form's values for the stored settings (a moment the server does not list is on). */
export function settingsFormValues(settings: MatchPushSettings): SettingsForm {
  return {
    dailyCapPerPlayer: settings.dailyCapPerPlayer,
    windowStartHour: settings.windowStartHour,
    windowEndHour: settings.windowEndHour,
    holdoutPercent: settings.holdoutPercent,
    maxInactiveDays: settings.maxInactiveDays,
    skipActiveMinutes: settings.skipActiveMinutes,
    moments: Object.fromEntries(PLAN_MOMENTS.map((m) => [m, settings.moments?.[m] !== false])),
    languageMode: settings.languageMode,
  };
}

const NUMBER_FIELDS = [
  'dailyCapPerPlayer',
  'windowStartHour',
  'windowEndHour',
  'holdoutPercent',
  'maxInactiveDays',
  'skipActiveMinutes',
] as const;

/**
 * The PUT body: only the fields that differ from what was loaded. The backend
 * patches field by field (moments one at a time) and checks the super admin
 * rule on what is sent, so sending untouched fields would undo another
 * admin's change or fail on a field this admin never touched.
 */
export function settingsChanges(
  loaded: SettingsForm,
  values: SettingsForm
): UpdateMatchPushSettingsBody {
  const body: UpdateMatchPushSettingsBody = {};
  for (const key of NUMBER_FIELDS) {
    if (values[key] !== loaded[key]) body[key] = values[key];
  }
  if (values.languageMode !== loaded.languageMode) body.languageMode = values.languageMode;
  const moments: Record<string, boolean> = {};
  for (const moment of PLAN_MOMENTS) {
    if (values.moments?.[moment] !== loaded.moments?.[moment]) {
      moments[moment] = values.moments?.[moment] !== false;
    }
  }
  if (Object.keys(moments).length > 0) body.moments = moments;
  return body;
}

/**
 * The changes only a super admin may make, because they make more pushes go
 * out (mirrors assertMayChange in match-push-admin.service.ts): a higher cap, a
 * wider window, a lower holdout, more inactive days, a lower active-player
 * skip, a moment switched on. Tightening is open to every admin.
 */
export function superAdminChanges(
  loaded: SettingsForm,
  changes: UpdateMatchPushSettingsBody
): string[] {
  const more: string[] = [];
  const higher = (v: number | undefined, was: number) => v !== undefined && v > was;
  const lower = (v: number | undefined, was: number) => v !== undefined && v < was;
  if (higher(changes.dailyCapPerPlayer, loaded.dailyCapPerPlayer)) more.push('raise the daily cap');
  if (
    lower(changes.windowStartHour, loaded.windowStartHour) ||
    higher(changes.windowEndHour, loaded.windowEndHour)
  ) {
    more.push('widen the send window');
  }
  if (lower(changes.holdoutPercent, loaded.holdoutPercent)) more.push('lower the holdout');
  if (higher(changes.maxInactiveDays, loaded.maxInactiveDays)) more.push('raise the inactive days');
  if (lower(changes.skipActiveMinutes, loaded.skipActiveMinutes)) {
    more.push('lower the active-player skip');
  }
  const on = PLAN_MOMENTS.filter(
    (m) => changes.moments?.[m] === true && loaded.moments?.[m] === false
  );
  if (on.length > 0) more.push(`switch on ${on.map((m) => MOMENT_LABEL[m]).join(', ')}`);
  return more;
}
